'use client';

import Image from 'next/image';
import { useMemo, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getPlaceholderImageUrl } from '@/features/media/domain/valueobject/image-presets';
import type { MenuSnapshotProduct } from '@/features/totems/domain/model/menu-snapshot';
import { getProductsByCategory } from '@/features/totems/domain/service/category-products';
import { getProductDisplayName } from '@/features/totems/domain/service/product-name';
import {
  getProductStockLimit,
  isProductOutOfStock,
  shouldShowProduct,
} from '@/features/totems/domain/service/product-stock';
import { formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import { useProductInteraction } from '@/features/totems/presentation/hooks/web-totem/use-product-interaction';
import { useTotemCartState, useTotemCatalog } from '@/features/totems/presentation/providers/web-totem/totem-provider';
import { cn } from '@/shared/utils/utils';

type ProductCardProps = {
  product: MenuSnapshotProduct;
  onSelect?: (product: MenuSnapshotProduct, rect: DOMRect) => void;
  compact?: boolean;
  readOnly?: boolean;
};

function TotemProductCard({ product, onSelect, compact, readOnly = false }: ProductCardProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const name = getProductDisplayName(product);
  const { cart } = useTotemCartState();

  const stockLimit = useMemo(() => getProductStockLimit(product.product_stock), [product.product_stock]);
  const isOutOfStock = stockLimit <= 0;
  const totalInCart = useMemo(() => {
    return cart.items.filter(i => i.productId === product.id).reduce((sum, i) => sum + i.quantity, 0);
  }, [cart.items, product.id]);

  const isAtLimit = totalInCart >= stockLimit;
  const isDisabled = isOutOfStock || isAtLimit;

  const priceFrom = useMemo(() => {
    if (Number(product.price) > 0) return 0;
    let min = 0;
    for (const ps of product.product_subselection ?? []) {
      const sub = ps.subselection;
      if (!sub) continue;
      for (const item of sub.subselection_item ?? []) {
        const isOutOfStockItem = isProductOutOfStock(item.product_stock);
        if (!isOutOfStockItem && item.price > 0 && (min === 0 || item.price < min)) {
          min = item.price;
        }
      }
    }
    return min;
  }, [product]);

  if (readOnly) {
    return (
      <div
        className={cn(
          'flex cursor-default flex-col items-center rounded-xl text-center select-none',
          compact ? 'gap-1 p-1 sm:gap-1.5 sm:p-1 lg:gap-3 lg:p-2' : 'gap-3 p-2'
        )}
      >
        <div
          className={cn(
            'relative aspect-square w-full overflow-hidden rounded-xl bg-white dark:bg-muted/30',
            compact ? 'max-w-24 sm:max-w-28 lg:max-w-32 xl:max-w-48' : 'max-w-48'
          )}
        >
          <Image
            alt={name}
            className={cn('object-contain p-2', (!product.imageurl || isOutOfStock) && 'opacity-60 grayscale')}
            fill
            sizes="(max-width: 768px) 33vw, 200px"
            src={product.imageurl || getPlaceholderImageUrl('product')}
          />
          {isOutOfStock && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40">
              <span className="rounded-md bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white shadow sm:text-xs">
                Sin stock
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p
            className={cn(
              'font-medium capitalize',
              compact ? 'text-[11px] leading-tight sm:text-xs lg:text-sm' : 'text-sm'
            )}
          >
            {name}
          </p>
          <p className={cn('font-bold text-amber-600', compact ? 'text-xs sm:text-sm lg:text-base' : 'text-base')}>
            {Number(product.price) === 0 && priceFrom > 0
              ? `Desde ${formatTotemPrice(priceFrom)}`
              : formatTotemPrice(Number(product.price))}
          </p>
        </div>
      </div>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      disabled={isDisabled}
      className={cn(
        'relative flex flex-col items-center rounded-xl text-center transition-opacity',
        isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:opacity-80',
        compact ? 'gap-1 p-1 sm:gap-1.5 sm:p-1 lg:gap-3 lg:p-2' : 'gap-3 p-2'
      )}
      onClick={() => {
        if (isDisabled) return;
        const rect = ref.current?.getBoundingClientRect();
        if (rect) onSelect?.(product, rect);
      }}
    >
      <div
        className={cn(
          'relative aspect-square w-full overflow-hidden rounded-xl bg-white dark:bg-muted/30',
          compact ? 'max-w-24 sm:max-w-28 lg:max-w-32 xl:max-w-48' : 'max-w-48'
        )}
      >
        <Image
          alt={name}
          className={cn('object-contain p-2', (!product.imageurl || isOutOfStock) && 'opacity-60 grayscale')}
          fill
          sizes="(max-width: 768px) 33vw, 200px"
          src={product.imageurl || getPlaceholderImageUrl('product')}
        />
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40">
            <span className="rounded-md bg-red-600 px-2 py-0.5 text-[11px] font-bold text-white shadow sm:text-xs">
              Sin stock
            </span>
          </div>
        )}
        {!isOutOfStock && isAtLimit && (
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40">
            <span className="rounded-md bg-amber-600 px-2 py-0.5 text-[11px] font-bold text-white shadow sm:text-xs">
              Límite alcanzado
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p
          className={cn(
            'font-medium capitalize',
            compact ? 'text-[11px] leading-tight sm:text-xs lg:text-sm' : 'text-sm'
          )}
        >
          {name}
        </p>
        <p className={cn('font-bold text-amber-600', compact ? 'text-xs sm:text-sm lg:text-base' : 'text-base')}>
          {Number(product.price) === 0 && priceFrom > 0
            ? `Desde ${formatTotemPrice(priceFrom)}`
            : formatTotemPrice(Number(product.price))}
        </p>
      </div>
    </button>
  );
}

interface TotemProductGridProps {
  readOnly?: boolean;
}

export function TotemProductGrid({ readOnly = false }: TotemProductGridProps) {
  const compact = true;

  const { activeCategoryName, activeCategoryId, menu, searchQuery } = useTotemCatalog();
  const { handleProductClick } = useProductInteraction();

  const activeSearch = searchQuery.trim().toLowerCase();

  const products = useMemo(() => {
    if (!menu?.products) return [];

    if (activeSearch) {
      return menu.products
        .filter(product => {
          if (!shouldShowProduct(product)) return false;
          return getProductDisplayName(product).toLowerCase().includes(activeSearch);
        })
        .sort((a, b) => getProductDisplayName(a).localeCompare(getProductDisplayName(b), 'es'));
    }

    if (!activeCategoryId) return [];

    return getProductsByCategory(menu.products, activeCategoryId);
  }, [menu?.products, activeSearch, activeCategoryId]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white dark:bg-transparent">
      <div
        className={cn(
          'min-h-16 shrink-0 items-center gap-4 px-6 pt-6',
          compact ? 'flex flex-col sm:grid sm:grid-cols-3' : 'grid grid-cols-3'
        )}
      >
        <h2 className={cn('w-full truncate text-left font-bold', compact ? 'text-lg sm:text-2xl' : 'text-2xl')}>
          {activeSearch ? `Resultados para "${searchQuery}" (${products.length})` : activeCategoryName}
        </h2>

        {/* Empty div for right column balance */}
        <div />
      </div>
      <ScrollArea className="min-h-0 flex-1 px-6 pt-4 pb-6">
        {products.length === 0 ? (
          <p className="text-muted-foreground">No hay productos en esta categoría.</p>
        ) : (
          <div
            className={cn(
              'grid',
              compact
                ? 'grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5'
                : 'grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'
            )}
          >
            {products.map(product => (
              <TotemProductCard
                key={product.id}
                product={product}
                compact={compact}
                readOnly={readOnly}
                onSelect={readOnly ? undefined : (p, rect) => handleProductClick(p, rect)}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
